import Vue from 'vue';

/**
 * WebSocket 连接管理模块
 */
const state = {
  // 存储多个 WebSocket 连接，格式：{ id: { connection, info: { id, name, ... }, isConnected, reconnectCount, lastConnectTime, lastError } }
  connections: {},
};

const mutations = {
  /**
   * 添加 WebSocket 连接
   */
  ADD_CONNECTION(state, { connection, info }) {
    // 如果存在同ID的旧连接，保存其状态信息
    const existingConnection = state.connections[info.id];
    const reconnectCount = existingConnection
      ? existingConnection.reconnectCount
      : 0;

    Vue.set(state.connections, info.id, {
      connection: connection,
      info: info,
      isConnected: false,
      reconnectCount: reconnectCount, // 保持重连计数
      lastConnectTime: null,
      lastError: null,
    });
  },

  /**
   * 移除 WebSocket 连接
   */
  REMOVE_CONNECTION(state, id) {
    if (state.connections[id]) {
      state.connections[id].connection.close();
      Vue.delete(state.connections, id);
    }
  },

  /**
   * 设置用户信息
   */
  SET_USER_INFO(state, { id, info }) {
    if (state.connections[id]) {
      state.connections[id].info = { ...state.connections[id].info, ...info };
    }
  },

  /**
   * 更新连接状态
   */
  UPDATE_CONNECTION_STATE(state, { id, status }) {
    if (state.connections[id]) {
      Object.assign(state.connections[id], status);
    }
  },

  /**
   * 清空所有连接
   */
  CLEAR_ALL_CONNECTIONS(state) {
    Object.keys(state.connections).forEach((id) => {
      if (state.connections[id]) {
        state.connections[id].connection.close();
      }
    });
    state.connections = {};
  },
};

const actions = {
  /**
   * 创建新的 WebSocket 连接，会：保留旧连接（如果有）→ 创建新连接 → 等待连接成功 → 关闭旧连接
   */
  createConnection({ commit, state }, { url, options = {}, info }) {
    return new Promise((resolve, reject) => {
      try {
        const enrichedInfo = {
          ...info,
          url: url,
          options: options,
        };

        const oldConnection = state.connections[info.id];

        // 动态导入 WebSocketClient 以避免循环依赖
        import('@/utils/socket')
          .then(({ default: WebSocketClient }) => {
            const newConnection = new WebSocketClient(url, options);

            // 设置新连接的事件监听器
            newConnection.on('open', () => {
              console.log('新连接建立成功', info.id);
              try {
                // 新连接成功建立后，安全地关闭旧连接
                if (oldConnection) {
                  // console.log(`新连接 ${info.id} 建立成功，安全关闭旧连接`);

                  // 标记旧连接为手动关闭，避免其事件监听器触发状态更新
                  if (oldConnection.connection.socket) {
                    oldConnection.connection.isManualClosed = true;
                  }

                  // 关闭旧连接
                  oldConnection.connection.close();
                }

                // 更新连接状态
                commit('UPDATE_CONNECTION_STATE', {
                  id: info.id,
                  status: {
                    isConnected: true,
                    lastConnectTime: new Date(),
                    lastError: null,
                  },
                });

                // 连接成功后才resolve
                // console.log('连接建立成功，resolve newConnection:', newConnection.isConnected);
                resolve(newConnection);
              } catch (error) {
                // console.error(`连接 ${info.id} open事件处理错误:`, error);
                reject(error);
              }
            });

            newConnection.on('close', (e) => {
              console.log('连接关闭', e);
              // 只有当前连接关闭时才更新状态
              const currentConnection = state.connections[info.id];
              if (
                currentConnection &&
                currentConnection.connection === newConnection
              ) {
                commit('UPDATE_CONNECTION_STATE', {
                  id: info.id,
                  status: {
                    isConnected: false,
                  },
                });
              }
            });

            newConnection.on('error', (error) => {
              console.log('连接错误', error);
              // 只有当前连接错误时才更新状态
              const currentConnection = state.connections[info.id];
              if (
                currentConnection &&
                currentConnection.connection === newConnection
              ) {
                commit('UPDATE_CONNECTION_STATE', {
                  id: info.id,
                  status: {
                    lastError: error,
                    failNum: currentConnection.failNum
                      ? currentConnection.failNum + 1
                      : 1,
                  },
                });

                // 连接错误达到最大时reject
                if (currentConnection.failNum >= 5) {
                  reject(error);
                }
              }
            });

            // 添加/替换连接到 store
            commit('ADD_CONNECTION', {
              connection: newConnection,
              info: enrichedInfo,
            });

            // 开始连接
            newConnection.connect();
            resolve(newConnection);
            // console.log('等待连接成功，当前isConnected:', newConnection.isConnected);
          })
          .catch((error) => {
            // 处理动态导入失败
            // console.error(`连接 ${info.id} 动态导入WebSocketClient失败:`, error);
            reject(error);
          });
      } catch (error) {
        reject(error);
      }
    });
  },

  /**
   * 仅创建连接信息存储，不实际连接socket
   * 用于存储用户信息、URL等连接相关数据，但不建立实际的WebSocket连接
   */
  createConnectionInfo({ commit, state }, { url, options = {}, info }) {
    return new Promise((resolve) => {
      try {
        const enrichedInfo = {
          ...info,
          url: url,
          options: options,
        };

        // 创建一个虚拟连接对象，仅用于存储信息
        const virtualConnection = {
          // 虚拟连接对象，不实际连接
          connected: false,
          isConnected: false,
          socket: null,
          close: () => {
            // console.log(`虚拟连接 ${info.id} 关闭`);
          },
          send: () => {
            // console.warn(`虚拟连接 ${info.id} 不支持发送消息`);
            return false;
          },
          connect: () => {
            // console.warn(`虚拟连接 ${info.id} 不支持连接操作`);
          },
          // 添加事件监听方法以保持接口一致性
          on: (event, handler) => {
            // console.log(`虚拟连接 ${info.id} 添加 ${event} 监听器`);
            // 虚拟连接不实际处理事件，但保持接口一致
          },
          off: (event, handler) => {
            // console.log(`虚拟连接 ${info.id} 移除 ${event} 监听器`);
            // 虚拟连接不实际处理事件，但保持接口一致
          },
        };

        // 添加/替换连接到 store，但使用虚拟连接对象
        commit('ADD_CONNECTION', {
          connection: virtualConnection,
          info: enrichedInfo,
        });

        // 更新连接状态为未连接
        commit('UPDATE_CONNECTION_STATE', {
          id: info.id,
          status: {
            isConnected: true,
            lastConnectTime: null,
            lastError: null,
          },
        });

        // console.log(`虚拟连接信息 ${info.id} 创建成功，仅存储信息，不建立实际连接`);
        resolve(virtualConnection);
      } catch (error) {
        // console.error(`创建虚拟连接信息 ${info.id} 失败:`, error);
        resolve(null);
      }
    });
  },

  /**
   * 重连指定的 WebSocket 连接（无缝重连）
   */
  reconnectConnection({ commit, state, dispatch }, id) {
    return new Promise(async (resolve, reject) => {
      const connectionObj = state.connections[id];
      if (!connectionObj) {
        throw new Error(`连接 ${id} 不存在`);
      }

      const { info } = connectionObj;
      if (!info.url) {
        throw new Error(`连接 ${id} 缺少重连所需的 URL 信息`);
      }

      // 更新重连次数
      commit('UPDATE_CONNECTION_STATE', {
        id,
        status: {
          reconnectCount: connectionObj.reconnectCount + 1,
        },
      });

      // console.log(`开始无缝重连 ${id}...`);

      // 使用无缝重连逻辑：先建立新连接再关闭旧连接
      await dispatch('createConnection', {
        url: info.url,
        options: info.options || {},
        info: info,
      })
        .then((res) => {
          // console.log('无缝重连成功', res);
          if (res.isConnected) {
            resolve(res);
          } else {
            reject(new Error(`连接 ${id} 无缝重连失败`));
          }
        })
        .catch((error) => {
          // console.log('无缝重连失败', error);
          reject(error);
        });
    });
  },

  /**
   * 关闭指定连接（但保留连接信息以便重连）
   */
  closeConnection({ commit, state }, id) {
    const connectionObj = state.connections[id];
    if (connectionObj && connectionObj.connection) {
      // 关闭 WebSocket 连接
      connectionObj.connection.close();

      // 更新连接状态为断开，但保留连接信息
      commit('UPDATE_CONNECTION_STATE', {
        id,
        status: {
          isConnected: false,
        },
      });
    }
  },

  /**
   * 关闭所有连接
   */
  closeAllConnections({ commit }) {
    commit('CLEAR_ALL_CONNECTIONS');
  },

  /**
   * 发送消息到指定连接
   */
  sendMessage({ state }, { id, data }) {
    const connectionObj = state.connections[id];
    if (
      connectionObj &&
      connectionObj.connection &&
      connectionObj.connection.connected
    ) {
      const res = connectionObj.connection.send(data);
      return res;
    }
  },
};

const getters = {
  /**
   * 根据id获取指定连接
   */
  getConnection: (state) => (id) => {
    return state.connections[id]?.connection;
  },
  /**
   * 根据id获取连接的用户信息
   */
  getUserInfo: (state) => (id) => {
    return state.connections[id]?.info;
  },
  /**
   * 根据id获取连接状态
   */
  getConnectionState: (state) => (id) => {
    const conn = state.connections[id];
    if (!conn) return null;
    return {
      isConnected: conn.isConnected,
      reconnectCount: conn.reconnectCount,
      lastConnectTime: conn.lastConnectTime,
      lastError: conn.lastError,
      info: conn.info,
    };
  },

  /**
   * 获取所有连接 ID
   */
  getAllConnectionIds: (state) => {
    return Object.keys(state.connections);
  },

  /**
   * 获取所有用户连接信息
   */
  getAllUserConnections: (state) => {
    return Object.keys(state.connections).map((id) => ({
      connectionId: id,
      info: state.connections[id].info,
      isConnected: state.connections[id].isConnected,
      lastConnectTime: state.connections[id].lastConnectTime,
    }));
  },

  /**
   * 获取已连接的连接数量
   */
  getConnectedCount: (state) => {
    return Object.values(state.connections).filter((conn) => conn.isConnected)
      .length;
  },

  /**
   * 检查指定连接是否已连接
   */
  isConnected: (state) => (id) => {
    return state.connections[id]?.isConnected || false;
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
